import axios, { AxiosInstance } from 'axios';

import getEnv from '@/utils/env';
import router from '@/router/router';
import { RouteNames } from '@/router/routes';
import { useProfileStore } from '@/store/profileStore';
import { EnvironmentTypes } from '@/types/environmentTypes';
import { AlertTypes, useAlertStore } from '@/store/alertsStore';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { logoutAndResetAllStoresWithCid } from '@/store/storeUtils';

axios.defaults.withCredentials = true; // allows cross domain cookies

const apiGw: AxiosInstance = axios.create({
  baseURL: `${getEnv(EnvironmentTypes.VITE_APP_GW_HOST)}/api`,
  headers: {
    'x-api-key': getEnv(EnvironmentTypes.VITE_APP_API_KEY)
    // ...(subjectID && { 'x-subject-uid': subjectID })
  }
});

apiGw.interceptors.request.use((request) => {
  const profileStore = useProfileStore();
  const profileLocale = profileStore.communication.locale;
  if (profileLocale) {
    request.headers.set('Accept-Language', profileLocale.substring(0, 2));
  }

  const contextSwitchStore = useContextSwitchStore();
  if (!contextSwitchStore.isPrivateContext && contextSwitchStore.currentContextUid) {
    request.headers.set('x-partnership-uid', contextSwitchStore.currentContextUid);
  }

  request.headers.set('x-origin', 'yapweb');
  request.headers.set('x-origin-version', getEnv(EnvironmentTypes.VITE_APP_VERSION));
  return request;
});

// respone Interceptor  ==>  Processing the response
apiGw.interceptors.response.use(
  (response) => {
    return response;
    //  If the custom return code is not equal to 200,  Go back 1 Errors
    // if (response.status !== 200) {
    //   console.log('res.code!==200', response.status, response);
    //   return Promise.reject(new Error(res.message || 'Error'));
    // } else {
    //   return res;
    // }
  },
  (error) => {
    let text = '';
    const err = JSON.parse(JSON.stringify(error));

    const alertStore = useAlertStore();
    const now = Date.now();

    if (err.status) {
      switch (err.status) {
        case 400:
          if (error.response.data.error?.name === 'ValidationError') {
            text = 'Validation Error';
            return Promise.reject({ ...error, ...error.response });
          } else if (!!error.response.data.code) {
            text = 'Submission Error';
            return Promise.reject({ ...error, ...error.response });
          }
          text = 'Request error (400)';
          break;
        case 401:
          text = 'Login error (401) Please log in again';
          alertStore.addAlert({
            id: 'technicalError',
            title: 'webapp.error.session_expired.title',
            message: 'webapp.error.session_expired.txt',
            dismissable: true,
            alertType: AlertTypes.Info,
            alertTimestamp: now
          });
          logoutAndResetAllStoresWithCid();
          router.replace({ name: RouteNames.Login });
          break;
        case 403:
          text = 'Access denied (403)';
          break;
        case 404:
          switch (error.response.data.code) {
            case 1125:
              text = 'Resource not found (404): not ready';
              break;
            case 2016:
              text = 'Resource not found (404)';
              break;
            default:
              text = 'Resource not found (404)';
              alertStore.addAlert({
                id: 'technicalError-404',
                title: 'app.error.technicalError.title',
                message: 'webapp.error.technicalError.txt',
                dismissable: true,
                alertType: AlertTypes.Error,
                alertTimestamp: now
              });
              break;
          }
        case 408:
          text = 'Request timeout (408)';
          break;
        case 413:
          text = 'Payload Too Large (413)';
          alertStore.addAlert({
            id: 'technicalError-413',
            title: 'webapp.error.payloadTooLarge.title',
            message: 'webapp.error.payloadTooLarge.txt',
            dismissable: true,
            alertType: AlertTypes.Error,
            alertTimestamp: now
          });
          break;
        case 422:
          text = 'Unprocessable Entity (422)';
          return Promise.reject({ ...error, ...error.response });
        case 500:
          text = 'Server error (500)';
          displayGenericError(alertStore);
          break;
        case 501:
          text = 'Service not implemented (501)';
          break;
        case 502:
          text = 'Network error (502)';
          break;
        case 503:
          text = 'Service unavailable (503)';
          break;
        case 504:
          text = 'Network timeout (504)';
          break;
        case 505:
          text = 'HTTP Version is not supported (505)';
          break;
        default:
          text = 'Network connection error';
      }
    } else {
      console.warn('test', error.message);

      text = 'Failed to connect to server';

      displayGenericError(alertStore);
    }

    return Promise.reject(error);
  }
);

export function displayGenericError(alertStore: any) {
  const now = Date.now();
  alertStore.addAlert({
    id: 'technicalError',
    title: 'app.error.technicalError.title',
    message: 'webapp.error.technicalError.txt',
    dismissable: true,
    alertType: AlertTypes.Error,
    alertTimestamp: now
  });
}

export default apiGw;
